import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { stringify } from 'query-string';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';

interface UseTableSortOptions {
  defaultSort?: { id: string; desc: boolean };
}

export default function useTableSort({
  defaultSort = { id: 'createdAt', desc: true },
}: UseTableSortOptions = {}) {
  const history = useHistory();
  const query = useUrlQuery();

  const sortBy = typeof query.sortBy === 'string' ? query.sortBy : undefined;
  const sortDirection = typeof query.sortDirection === 'string' ? query.sortDirection : undefined;

  const sorting: SortingState = useMemo(
    () => [sortBy ? { id: sortBy, desc: sortDirection === '-1' } : defaultSort],
    [sortBy, sortDirection, defaultSort.id, defaultSort.desc],
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0] ?? defaultSort;
      history.replace({
        search: stringify({
          ...query,
          sortBy: first.id,
          sortDirection: first.desc ? '-1' : '1',
          page: undefined,
        }),
      });
    },
    [history, query, sorting, defaultSort.id, defaultSort.desc],
  );

  const sort = useMemo(() => {
    const first = sorting[0] ?? defaultSort;
    return { [first.id]: first.desc ? '-1' : '1' };
  }, [sorting, defaultSort.id, defaultSort.desc]);

  return { sorting, onSortingChange, sort };
}
